import { Link } from '@inertiajs/react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import type { EmailsShow } from '@/types';
import { EmailPreview } from './components/EmailPreview';
import { ResendEmailButton } from './components/ResendEmailButton';

export default function Show({ email }: EmailsShow) {
  return (
    <main className="flex-1">
      <div className="container space-y-6 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-bold text-2xl">Email Record</h1>
            <p className="text-muted-foreground">
              {email.sentAt
                ? `Sent on ${new Date(email.sentAt).toLocaleDateString()}`
                : `Created on ${new Date(email.createdAt).toLocaleDateString()}`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ResendEmailButton emailId={email.id} />
            <Button variant="outline" asChild>
              <Link href="/emails">Back to List</Link>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle>Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <strong className="font-medium text-sm">Template:</strong>
                  <p className="text-muted-foreground text-sm">
                    {email.templateName}
                  </p>
                </div>
                <div>
                  <strong className="font-medium text-sm">To:</strong>
                  <p className="text-muted-foreground text-sm">{email.to}</p>
                </div>
                {email.cc && email.cc.length > 0 && (
                  <div>
                    <strong className="font-medium text-sm">CC:</strong>
                    <p className="text-muted-foreground text-sm">
                      {email.cc.join(', ')}
                    </p>
                  </div>
                )}
                {email.bcc && email.bcc.length > 0 && (
                  <div>
                    <strong className="font-medium text-sm">BCC:</strong>
                    <p className="text-muted-foreground text-sm">
                      {email.bcc.join(', ')}
                    </p>
                  </div>
                )}
                <div>
                  <strong className="font-medium text-sm">Subject:</strong>
                  <p className="text-muted-foreground text-sm">
                    {email.subject}
                  </p>
                </div>
                <div>
                  <strong className="font-medium text-sm">Status:</strong>
                  <div className="mt-1">
                    <span
                      className={`inline-flex rounded-full px-2 py-1 font-medium text-xs ${
                        email.status === 'sent'
                          ? 'bg-green-100 text-green-800'
                          : email.status === 'failed'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {email.status}
                    </span>
                  </div>
                </div>
                {email.failureReason && (
                  <div>
                    <strong className="font-medium text-red-600 text-sm">
                      Failure Reason:
                    </strong>
                    <p className="mt-1 text-red-600 text-sm">
                      {email.failureReason}
                    </p>
                  </div>
                )}
                <div>
                  <strong className="font-medium text-sm">Message ID:</strong>
                  {email.externalMessageId ? (
                    <p className="font-mono text-muted-foreground text-sm">
                      {email.externalMessageId}
                    </p>
                  ) : (
                    <p className="text-muted-foreground text-sm italic">
                      No external message ID available
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-2">
            <EmailPreview htmlContent={email.htmlContent} />
          </div>
        </div>
      </div>
    </main>
  );
}
